import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import translations from 'services/translations/translations';

import 'style/contacts/CreditCheckBadge.css';

// The credit information is fresh for three months after it was checked
const FRESH_MONTHS = 3;

/** Whether the credit information was checked within the last three months */
export const isCreditCheckFresh = (
  checkedAt: null | string | undefined,
  today: Date = new Date()
): boolean => {
  if (!checkedAt) {
    return false;
  }

  const checked = new Date(checkedAt);
  if (Number.isNaN(checked.getTime())) {
    return false;
  }

  const limit = new Date(today);
  limit.setMonth(limit.getMonth() - FRESH_MONTHS);
  limit.setHours(0, 0, 0, 0);
  return checked.getTime() >= limit.getTime();
};

/**
 * A green tick after the name of a customer whose credit information was checked within
 * three months, else a red cross. A click opens the request to check the credit
 * information; the request is not sent to the financial administration yet.
 */
const CreditCheckBadge = ({
  checkedAt,
  companyName,
}: {
  checkedAt: null | string | undefined;
  companyName: null | string | undefined;
}): JSX.Element => {
  const { t, i18n } = useTranslation([translations.common]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.common}:creditCheck.${key}`, options);
  const [open, setOpen] = useState(false);

  const fresh = isCreditCheckFresh(checkedAt);
  const date = checkedAt
    ? new Date(checkedAt).toLocaleDateString(i18n.language)
    : null;
  const title =
    date === null
      ? tr('Never checked')
      : tr(fresh ? 'Checked' : 'Checked long ago', { date });

  return (
    // The clicks of the window too stay here: drawn on the page, they still reach the row
    // the badge is on (e.g. a row of the offer list opens its offer)
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <span onClick={(event) => event.stopPropagation()}>
      <button
        aria-label={title}
        className={`credit-check-badge ${fresh ? 'fresh' : 'stale'}`}
        onClick={(event) => {
          // A click on the badge does not open the offer or the row it is on
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        title={title}
        type="button"
      >
        {fresh ? '✓' : '✗'}
      </button>
      <Modal isOpen={open} toggle={() => setOpen(false)}>
        <ModalHeader toggle={() => setOpen(false)}>
          {tr('Request title')}
        </ModalHeader>
        <ModalBody>
          <p>{tr('Request text', { name: companyName ?? '' })}</p>
          <p className={`credit-check-status ${fresh ? 'fresh' : 'stale'}`}>
            {title}
          </p>
          <p className="credit-check-note">{tr('Not sent yet')}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setOpen(false)}>
            {t(`${translations.common}:Cancel`)}
          </Button>
          {/* Sends the request to the financial administration once that is in use */}
          <Button className="primary-button" onClick={() => setOpen(false)}>
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </span>
  );
};

export default CreditCheckBadge;
